import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getTag, createTag, updateTag } from '../../../services/tagService';
import { getCategories } from '../../../services/categoryService';
import { getSubcategories } from '../../../services/subcategoryService';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiUpload, FiChevronDown, FiChevronUp, FiTrash2, FiCheck, FiPlus } from 'react-icons/fi';
import Papa from 'papaparse';

const TagsFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        subcategory: '',
        isActive: true
    });

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Bulk upload states
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkTags, setBulkTags] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [submittingIndex, setSubmittingIndex] = useState(null);
    const [isSubmittingAll, setIsSubmittingAll] = useState(false);
    const [bulkSubcategoriesMap, setBulkSubcategoriesMap] = useState({});

    useEffect(() => {
        fetchCategories();
        if (isEditMode) {
            fetchTag();
        }
    }, [id]);

    useEffect(() => {
        if (formData.category) {
            fetchSubcategories(formData.category);
        } else {
            setSubcategories([]);
            setFormData(prev => ({ ...prev, subcategory: '' }));
        }
    }, [formData.category]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data || []);

            // Pre-fetch subcategories for all categories
            const cats = response.data || [];
            for (const category of cats) {
                if (!bulkSubcategoriesMap[category._id]) {
                    fetchSubcategories(category._id);
                }
            }

            return cats;
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
            return [];
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await getSubcategories(categoryId);
            const subcats = response.data || [];

            if (categoryId === formData.category) {
                setSubcategories(subcats);
            }

            // Store these subcategories in our map for bulk operations
            setBulkSubcategoriesMap(prev => ({
                ...prev,
                [categoryId]: subcats
            }));

            return subcats;
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            toast.error('Failed to load subcategories');
            return [];
        }
    };

    const fetchTag = async () => {
        try {
            setLoading(true);
            const response = await getTag(id);
            const tag = response.data;

            const categoryId = tag.category?._id || tag.category || '';
            const subcategoryId = tag.subcategory?._id || tag.subcategory || '';

            setFormData({
                name: tag.name || '',
                category: categoryId,
                subcategory: subcategoryId,
                isActive: tag.isActive !== undefined ? tag.isActive : true
            });

            if (categoryId) {
                fetchSubcategories(categoryId);
            }
        } catch (error) {
            console.error('Error fetching tag:', error);
            toast.error('Failed to load tag data');
            navigate('/admin/tags');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'category') {
            // When category changes, clear subcategory
            setFormData(prev => ({
                ...prev,
                category: value,
                subcategory: ''  // Reset subcategory when category changes
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            toast.error('Tag name is required');
            return false;
        }

        if (!formData.category) {
            toast.error('Category is required');
            return false;
        }

        if (!formData.subcategory) {
            toast.error('Subcategory is required');
            return false;
        }

        // Find the selected subcategory
        const selectedSubcategory = subcategories.find(sub => sub._id === formData.subcategory);
        if (!selectedSubcategory) {
            toast.error('Selected subcategory not found');
            return false;
        }

        // Check if the subcategory belongs to the selected category
        // Handle both cases where subcategory.category could be an object or a string
        const subcategoryCategory = typeof selectedSubcategory.category === 'object'
            ? selectedSubcategory.category._id
            : selectedSubcategory.category;

        if (subcategoryCategory !== formData.category) {
            toast.error('Selected subcategory does not belong to the selected category');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);

            // All fields are required, so no need to handle optional fields
            const submitData = { ...formData };

            if (isEditMode) {
                await updateTag(id, submitData);
                toast.success(`Tag "${formData.name}" updated successfully`);
            } else {
                await createTag(submitData);
                toast.success(`Tag "${formData.name}" created successfully`);
            }

            navigate('/admin/tags');
        } catch (error) {
            console.error('Error saving tag:', error);
            toast.error(error.response?.data?.message || 'Failed to save tag');
        } finally {
            setSubmitting(false);
        }
    };

    // Helper function to get subcategories for a tag
    const getSubcategoriesForTag = (tag) => {
        if (!tag || !tag.category) return [];
        return bulkSubcategoriesMap[tag.category] || [];
    };

    // Bulk upload functions
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    console.log("Parsed CSV data:", results.data);

                    // Ensure we have categories loaded
                    let cats = categories;
                    if (categories.length === 0) {
                        cats = await fetchCategories();
                    }

                    // Prefetch all subcategories for each category to avoid async issues
                    const prefetchPromises = [];
                    for (const category of cats) {
                        if (!bulkSubcategoriesMap[category._id]) {
                            prefetchPromises.push(fetchSubcategories(category._id));
                        }
                    }

                    // Wait for all subcategories to be fetched
                    if (prefetchPromises.length > 0) {
                        await Promise.all(prefetchPromises);
                    }

                    // Process each row
                    const processedTags = [];

                    for (let i = 0; i < results.data.length; i++) {
                        const item = results.data[i];

                        // Resolve category
                        let categoryId = '';
                        let categoryName = item.category || '';

                        if (categoryName) {
                            // Try exact match first
                            const foundCategory = cats.find(c =>
                                c.name.toLowerCase() === categoryName.trim().toLowerCase()
                            );

                            if (foundCategory) {
                                categoryId = foundCategory._id;
                            }
                        }

                        // Get available subcategories
                        let availableSubcategories = [];
                        if (categoryId && bulkSubcategoriesMap[categoryId]) {
                            availableSubcategories = bulkSubcategoriesMap[categoryId];
                        }

                        // Resolve subcategory
                        let subcategoryId = '';
                        let subcategoryName = item.subcategory || '';

                        if (categoryId && subcategoryName) {
                            // Try to find subcategory by name - with flexible matching
                            const normalizedSubcatName = subcategoryName.trim().toLowerCase();

                            // Try exact match first
                            let foundSubcategory = availableSubcategories.find(s =>
                                s.name.toLowerCase() === normalizedSubcatName
                            );

                            // If no exact match, try partial matches
                            if (!foundSubcategory) {
                                foundSubcategory = availableSubcategories.find(s =>
                                    s.name.toLowerCase().includes(normalizedSubcatName) ||
                                    normalizedSubcatName.includes(s.name.toLowerCase())
                                );
                            }

                            if (foundSubcategory) {
                                subcategoryId = foundSubcategory._id;
                                // Use the actual name from database for consistency
                                subcategoryName = foundSubcategory.name;
                            }
                        }

                        processedTags.push({
                            id: `temp-${i}`,
                            name: item.name?.trim() || '',
                            category: categoryId,
                            categoryName,
                            subcategory: subcategoryId,
                            subcategoryName,
                            isActive: item.isActive ? item.isActive.toLowerCase() === 'true' : true,
                            status: 'pending'
                        });
                    }

                    setBulkTags(processedTags);

                    // Display upload stats
                    const matchedCategories = processedTags.filter(t => t.category).length;
                    const matchedSubcategories = processedTags.filter(t => t.subcategory).length;
                    const total = processedTags.length;

                    toast.success(`Parsed ${total} tags. Matched ${matchedCategories} categories and ${matchedSubcategories} subcategories.`);

                    // Warn about unmatched items
                    if (matchedCategories < total) {
                        toast.warning(`${total - matchedCategories} categories couldn't be matched. Please select them manually.`);
                    }

                    if (matchedSubcategories < matchedCategories) {
                        toast.warning(`${matchedCategories - matchedSubcategories} subcategories couldn't be matched. Please select them manually.`);
                    }
                } catch (error) {
                    console.error('Error processing tags:', error);
                    toast.error('Error processing tags: ' + error.message);
                } finally {
                    setIsUploading(false);
                }
            },
            error: (error) => {
                console.error('CSV Parse Error:', error);
                toast.error('Error parsing CSV file: ' + error.message);
                setIsUploading(false);
            }
        });

        // Reset the file input
        e.target.value = null;
    };

    const handleBulkInputChange = async (index, field, value) => {
        const updatedTags = [...bulkTags];

        if (field === 'isActive') {
            updatedTags[index].isActive = value;
        } else if (field === 'category') {
            // When category changes, clear subcategory
            updatedTags[index].category = value;
            updatedTags[index].subcategory = '';
            updatedTags[index].subcategoryName = '';

            // Fetch subcategories for this category if not already in our map
            if (value) {
                try {
                    if (!bulkSubcategoriesMap[value]) {
                        await fetchSubcategories(value);
                    }

                    // Find the matching category name
                    const category = categories.find(c => c._id === value);
                    if (category) {
                        updatedTags[index].categoryName = category.name;
                    }
                } catch (error) {
                    console.error('Error fetching subcategories:', error);
                }
            } else {
                updatedTags[index].categoryName = '';
            }
        } else if (field === 'subcategory') {
            updatedTags[index].subcategory = value;

            // Find and set the subcategory name
            if (value) {
                const subcats = getSubcategoriesForTag(updatedTags[index]);
                const subcategory = subcats.find(s => s._id === value);
                if (subcategory) {
                    updatedTags[index].subcategoryName = subcategory.name;
                }
            } else {
                updatedTags[index].subcategoryName = '';
            }
        } else {
            updatedTags[index][field] = value;
        }

        setBulkTags(updatedTags);
    };

    const handleAddEmptyRow = () => {
        setBulkTags([
            ...bulkTags,
            {
                id: `temp-${Date.now()}`,
                name: '',
                category: '',
                categoryName: '',
                subcategory: '',
                subcategoryName: '',
                isActive: true,
                status: 'pending'
            }
        ]);
    };

    const handleRemoveRow = (index) => {
        const updatedTags = [...bulkTags];
        updatedTags.splice(index, 1);
        setBulkTags(updatedTags);
    };

    const validateBulkTag = (tag) => {
        if (!tag.name.trim()) {
            return { valid: false, message: 'Tag name is required' };
        }
        if (!tag.category) {
            return { valid: false, message: 'Category is required' };
        }
        if (!tag.subcategory) {
            return { valid: false, message: 'Subcategory is required' };
        }

        // Check if category exists
        const categoryExists = categories.some(c => c._id === tag.category);
        if (!categoryExists) {
            return { valid: false, message: 'Selected category doesn\'t exist' };
        }

        // Check if subcategory exists and belongs to the category
        if (bulkSubcategoriesMap[tag.category]) {
            const subcategoryExists = bulkSubcategoriesMap[tag.category].some(s => s._id === tag.subcategory);
            if (!subcategoryExists) {
                return { valid: false, message: 'Selected subcategory doesn\'t exist or is not part of the selected category' };
            }
        } else {
            return { valid: false, message: 'Category subcategories not loaded yet' };
        }

        return { valid: true };
    };

    const handleSubmitRow = async (index) => {
        const tag = bulkTags[index];
        const validation = validateBulkTag(tag);

        if (!validation.valid) {
            toast.error(`Row ${index + 1}: ${validation.message}`);
            return;
        }

        setSubmittingIndex(index);

        try {
            const updatedTags = [...bulkTags];
            updatedTags[index].status = 'submitting';
            setBulkTags(updatedTags);

            await createTag({
                name: tag.name,
                category: tag.category,
                subcategory: tag.subcategory,
                isActive: tag.isActive
            });

            updatedTags[index].status = 'success';
            setBulkTags(updatedTags);
            toast.success(`Tag "${tag.name}" created successfully`);
        } catch (error) {
            console.error('Error creating tag:', error);
            const updatedTags = [...bulkTags];
            updatedTags[index].status = 'error';
            updatedTags[index].errorMessage = error.response?.data?.message || 'Failed to create tag';
            setBulkTags(updatedTags);
            toast.error(`Failed to create tag "${tag.name}": ${updatedTags[index].errorMessage}`);
        } finally {
            setSubmittingIndex(null);
        }
    };

    const handleSubmitAll = async () => {
        if (bulkTags.length === 0) {
            toast.warning('No tags to submit');
            return;
        }

        // Validate all tags first
        for (let i = 0; i < bulkTags.length; i++) {
            const tag = bulkTags[i];
            if (tag.status === 'success') continue; // Skip already successful ones

            const validation = validateBulkTag(tag);
            if (!validation.valid) {
                toast.error(`Row ${i + 1}: ${validation.message}`);
                return;
            }
        }

        setIsSubmittingAll(true);

        try {
            const updatedTags = [...bulkTags];
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < updatedTags.length; i++) {
                const tag = updatedTags[i];

                // Skip already processed tags
                if (tag.status === 'success') {
                    successCount++;
                    continue;
                }

                try {
                    setSubmittingIndex(i);

                    // Update status to submitting
                    updatedTags[i].status = 'submitting';
                    setBulkTags([...updatedTags]);

                    await createTag({
                        name: tag.name,
                        category: tag.category,
                        subcategory: tag.subcategory,
                        isActive: tag.isActive
                    });

                    updatedTags[i].status = 'success';
                    successCount++;
                } catch (error) {
                    console.error('Error creating tag:', error);
                    updatedTags[i].status = 'error';
                    updatedTags[i].errorMessage = error.response?.data?.message || 'Failed to create tag';
                    errorCount++;
                }

                // Update the state after each operation
                setBulkTags([...updatedTags]);
            }

            if (successCount > 0) {
                toast.success(`Successfully created ${successCount} tags`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to create ${errorCount} tags`);
            }
        } finally {
            setIsSubmittingAll(false);
            setSubmittingIndex(null);
        }
    };

    const downloadSampleCSV = () => {
        const headers = ['name', 'category', 'subcategory', 'isActive'];

        // Get real category and subcategory names for the sample if available
        const categoryName1 = categories.length > 0 ? categories[0].name : 'Skincare';
        const categoryName2 = categories.length > 1 ? categories[1].name : 'Hair Care';

        // Try to get a subcategory for each category
        let subcategoryName1 = 'Cleansers';
        let subcategoryName2 = 'Shampoos';

        if (categories.length > 0 && bulkSubcategoriesMap[categories[0]._id]?.length > 0) {
            subcategoryName1 = bulkSubcategoriesMap[categories[0]._id][0].name;
        }

        if (categories.length > 1 && bulkSubcategoriesMap[categories[1]._id]?.length > 0) {
            subcategoryName2 = bulkSubcategoriesMap[categories[1]._id][0].name;
        }

        const sampleData = [
            {
                name: 'Anti-Aging',
                category: categoryName1, // Using category name instead of ID
                subcategory: subcategoryName1, // Using subcategory name instead of ID
                isActive: 'true'
            },
            {
                name: 'Sensitive',
                category: categoryName2, // Using category name instead of ID
                subcategory: subcategoryName2, // Using subcategory name instead of ID
                isActive: 'true'
            }
        ];

        const csvContent = Papa.unparse({
            fields: headers,
            data: sampleData
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'tag_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearBulkData = () => {
        if (window.confirm('Are you sure you want to clear all bulk data?')) {
            setBulkTags([]);
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? 'Edit Tag' : 'Create Tag'}
                </h1>

                {!isEditMode && (
                    <button
                        type="button"
                        className="btn btn-outline flex items-center"
                        onClick={() => setShowBulkUpload(!showBulkUpload)}
                    >
                        {showBulkUpload ? <FiChevronUp className="mr-2" /> : <FiChevronDown className="mr-2" />}
                        {showBulkUpload ? 'Hide Bulk Upload' : 'Show Bulk Upload'}
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {/* Single Tag Form */}
                    {(!showBulkUpload || isEditMode) && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tag Name*
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category*
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        >
                                            <option value="">Select a Category</option>
                                            {categories.map(category => (
                                                <option key={category._id} value={category._id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Subcategory*
                                        </label>
                                        <select
                                            name="subcategory"
                                            value={formData.subcategory}
                                            onChange={handleChange}
                                            className="input"
                                            disabled={!formData.category}
                                            required
                                        >
                                            <option value="">Select a Subcategory</option>
                                            {subcategories.map(subcategory => (
                                                <option key={subcategory._id} value={subcategory._id}>
                                                    {subcategory.name}
                                                </option>
                                            ))}
                                        </select>
                                        {!subcategories.length && formData.category && (
                                            <p className="mt-1 text-xs text-red-500">
                                                No subcategories found for this category
                                            </p>
                                        )}
                                    </div>

                                    <div className="col-span-2">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/tags')}
                                        className="btn btn-outline flex items-center"
                                    >
                                        <FiX className="mr-2" /> Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`btn btn-primary flex items-center ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FiSave className="mr-2" /> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Bulk Upload Section */}
                    {showBulkUpload && !isEditMode && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Bulk Create Tags</h2>
                                <div className="mb-6">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Upload a CSV file with tag data or add entries manually. The CSV should include columns for name, category, subcategory, and isActive.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="btn btn-primary flex items-center cursor-pointer">
                                            <FiUpload className="mr-2" />
                                            Upload CSV
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                disabled={isUploading}
                                            />
                                        </label>

                                        <button
                                            onClick={downloadSampleCSV}
                                            className="btn btn-outline flex items-center text-sm"
                                        >
                                            <FiUpload className="mr-1" /> Download Template
                                        </button>

                                        <button
                                            onClick={handleAddEmptyRow}
                                            className="btn btn-outline flex items-center"
                                        >
                                            <FiPlus className="mr-2" /> Add Row
                                        </button>

                                        {bulkTags.length > 0 && (
                                            <>
                                                <button
                                                    onClick={clearBulkData}
                                                    className="btn btn-outline flex items-center text-red-600 border-red-600 hover:bg-red-50"
                                                >
                                                    <FiTrash2 className="mr-2" /> Clear All
                                                </button>

                                                <button
                                                    onClick={handleSubmitAll}
                                                    disabled={isSubmittingAll || bulkTags.length === 0}
                                                    className={`btn btn-primary flex items-center ml-auto ${isSubmittingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                >
                                                    {isSubmittingAll ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                            Saving All...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiSave className="mr-2" /> Save All
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {isUploading ? (
                                    <div className="text-center py-10">
                                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mb-4"></div>
                                        <p>Parsing CSV...</p>
                                    </div>
                                ) : bulkTags.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Category*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Subcategory*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Active
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                                {bulkTags.map((tag, index) => (
                                                    <tr key={tag.id} className={`${tag.status === 'error' ? 'bg-red-50' : tag.status === 'success' ? 'bg-green-50' : ''}`}>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={tag.name}
                                                                onChange={(e) => handleBulkInputChange(index, 'name', e.target.value)}
                                                                disabled={tag.status === 'success' || tag.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm"
                                                                placeholder="Tag name"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                value={tag.category}
                                                                onChange={(e) => handleBulkInputChange(index, 'category', e.target.value)}
                                                                disabled={tag.status === 'success' || tag.status === 'submitting' || submittingIndex === index}
                                                                className={`input py-1 px-2 text-sm ${!tag.category && tag.categoryName ? 'border-yellow-400' : ''}`}
                                                                required
                                                            >
                                                                <option value="">Select Category</option>
                                                                {categories.map(category => (
                                                                    <option key={category._id} value={category._id}>
                                                                        {category.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!tag.category && tag.categoryName && (
                                                                <p className="mt-1 text-xs text-yellow-600">
                                                                    Could not find "{tag.categoryName}"
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                value={tag.subcategory}
                                                                onChange={(e) => handleBulkInputChange(index, 'subcategory', e.target.value)}
                                                                disabled={!tag.category || tag.status === 'success' || tag.status === 'submitting' || submittingIndex === index}
                                                                className={`input py-1 px-2 text-sm ${!tag.subcategory && tag.subcategoryName ? 'border-yellow-400' : ''}`}
                                                                required
                                                            >
                                                                <option value="">Select Subcategory</option>
                                                                {getSubcategoriesForTag(tag).map(subcategory => (
                                                                    <option key={subcategory._id} value={subcategory._id}>
                                                                        {subcategory.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!tag.subcategory && tag.subcategoryName && (
                                                                <p className="mt-1 text-xs text-yellow-600">
                                                                    Could not find "{tag.subcategoryName}"
                                                                </p>
                                                            )}
                                                            {getSubcategoriesForTag(tag).length === 0 && tag.category && (
                                                                <p className="mt-1 text-xs text-red-500">
                                                                    No subcategories found for this category
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={tag.isActive}
                                                                onChange={(e) => handleBulkInputChange(index, 'isActive', e.target.checked)}
                                                                disabled={tag.status === 'success' || tag.status === 'submitting' || submittingIndex === index}
                                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {tag.status === 'submitting' || submittingIndex === index ? (
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium">
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-primary-600 mr-1"></div>
                                                                    Submitting...
                                                                </span>
                                                            ) : tag.status === 'success' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <FiCheck className="mr-1" />
                                                                    Success
                                                                </span>
                                                            ) : tag.status === 'error' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={tag.errorMessage}>
                                                                    <FiX className="mr-1" />
                                                                    Error
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex space-x-2">
                                                                {tag.status !== 'success' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleSubmitRow(index)}
                                                                            disabled={tag.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-primary-600 hover:text-primary-900 p-1"
                                                                            title="Save this tag"
                                                                        >
                                                                            <FiSave size={16} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveRow(index)}
                                                                            disabled={tag.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-red-600 hover:text-red-900 p-1"
                                                                            title="Remove this row"
                                                                        >
                                                                            <FiTrash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No tags added yet. Upload a CSV or add rows manually.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </AdminLayout>
    );
};

export default TagsFormPage;